import { ChangeVerification, GoogleSearchEvidence } from '@/components/HealthEvidence';
import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, useLocation, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Search, Brain, Shield, FileText, TrendingUp, Download, Globe, Plus, Zap, LogIn, Lock } from "lucide-react";
import { GradeBadge } from "@/components/ScoreCard";
import { PaywallPanel } from "@/components/PaywallPanel";
import BusinessReport from "@/components/BusinessReport";
import { AppLayout } from "@/components/AppLayout";
import GlobalNav from "@/components/GlobalNav";
import { useAuth } from "@/context/AuthContext";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LoadingSkeleton = () => (
  <div className="p-6">
    <div className="max-w-7xl mx-auto space-y-6">
      <Skeleton className="h-16 w-96 bg-[#1A1A1A]" />
      <div className="grid grid-cols-5 gap-4">
        {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-28 bg-[#1A1A1A]" />)}
      </div>
      <Skeleton className="h-64 bg-[#1A1A1A]" />
    </div>
  </div>
);

export default function ReportDashboard({ staticData }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [report, setReport] = useState(staticData || null);
  const [loading, setLoading] = useState(!staticData);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const isDemo = !!staticData;
  const effectiveId = isDemo ? report?.id || 'sample' : id;
  const reportRef = useRef(null);

  // Track click if ?track=1 parameter is present
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('track') === '1' && id) {
      // Fire tracking API call (fire-and-forget)
      axios.post(`${API}/audit/${id}/track-click`).catch(() => {});
      // Remove ?track=1 from URL to prevent re-tracking on refresh
      navigate(`/report/${id}`, { replace: true });
    }
  }, [id, location.search, navigate]);

  useEffect(() => {
    if (staticData) return;
    const fetchReport = async () => {
      try {
        const res = await axios.get(`${API}/audit/${id}/report`);
        setReport(res.data);
      } catch (err) {
        if (err.response?.status === 400) navigate(`/audit/${id}`);
      } finally {
        setLoading(false);
      }
    };
    fetchReport();
  }, [id, navigate, staticData]);

  if (loading) return <LoadingSkeleton />;
  if (!report) return <div className="min-h-screen flex items-center justify-center text-zinc-400" style={{ background: '#0B0F14' }}>Report not found</div>;

  const { overall, request, crawl } = report;
  const scores = overall?.scores || {};
  const isFreeUser = report.is_free_user === true;  // Free tier user with gated Action Plan
  const hasBusinessReport = !!report.business_analysis && !report.business_analysis._error;

  const crawlCoverage = crawl?.coverage || crawl?.target || {};
  const pagesAnalyzed = crawlCoverage.analyzed_urls
    || crawlCoverage.pages_crawled
    || crawlCoverage.analyzed
    || report.business_analysis?.scores?.pages_scanned
    || 0;

  // Timestamp: generated_at preferred, fallback to created_at
  const auditTimestamp = report.generated_at || report.created_at;
  const auditDateStr = auditTimestamp
    ? new Date(auditTimestamp).toLocaleString('pl-PL', {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit'
      })
    : '';

  // Language and Region from audit parameters
  const auditLanguage = report.language || 'en';
  const auditRegion = report.region || 'UK';
  
  const LANGUAGE_LABELS = {
    en: 'English', pl: 'Polski', de: 'Deutsch', fr: 'Français',
    es: 'Español', it: 'Italiano', nl: 'Nederlands', pt: 'Português'
  };
  const REGION_LABELS = {
    UK: '🇬🇧 UK', US: '🇺🇸 USA', EU: '🇪🇺 EU', DE: '🇩🇪 Germany',
    FR: '🇫🇷 France', PL: '🇵🇱 Poland', ES: '🇪🇸 Spain', IT: '🇮🇹 Italy',
    NL: '🇳🇱 Netherlands', AU: '🇦🇺 Australia', CA: '🇨🇦 Canada', Global: '🌍 Global'
  };
  const languageLabel = LANGUAGE_LABELS[auditLanguage] || auditLanguage.toUpperCase();
  const regionLabel = REGION_LABELS[auditRegion] || auditRegion;

  const exportToPdf = async () => {
    try {
      const pdfUrl = isDemo
        ? `${API}/sample-report/pdf`
        : `${API}/audit/${id}/pdf`;
      const res = await fetch(pdfUrl);
      if (!res.ok) throw new Error('PDF generation failed');
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const domain = report?.request?.target?.normalized_domain || 'report';
      link.download = `scorecite-${domain}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error('PDF download error:', e);
    }
  };

  // Get domain name for breadcrumbs
  const domainName = report?.request?.target?.normalized_domain || 'Report';
  const auditDate = report?.request?.start_time 
    ? new Date(report.request.start_time).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
    : '';

  // Wrap in AppLayout for logged-in users, or show plain for sample report
  const content = (
    <div className="overflow-x-hidden" data-testid="report-dashboard" ref={reportRef}>
      <div className="w-full max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 pt-4 pb-12 overflow-hidden">

        {/* Domain Header */}
        <div className="text-center mb-6">
          <div className="flex items-center justify-center gap-3">
            <h1 className="font-heading text-xl sm:text-3xl font-black tracking-tight text-white break-words" data-testid="report-domain">
              {request?.target?.normalized_domain}
            </h1>
            <GradeBadge grade={overall?.grade} />
          </div>
          <p className="text-zinc-500 text-sm mt-1">
            full audit &middot; {pagesAnalyzed > 0 ? `${pagesAnalyzed} pages` : 'pages loading...'} &middot; {auditDateStr}
          </p>
          {/* Audit Parameters Stamp */}
          <div className="flex items-center justify-center gap-2 mt-2" data-testid="audit-params-stamp">
            <Badge variant="outline" className="text-xs border-zinc-700 text-zinc-400 bg-zinc-900/50">
              <Globe className="w-3 h-3 mr-1" />
              {languageLabel}
            </Badge>
            <Badge variant="outline" className="text-xs border-zinc-700 text-zinc-400 bg-zinc-900/50">
              {regionLabel}
            </Badge>
            {report.request?.restrict_to_path && (
              <Badge variant="outline" className="text-xs border-yellow-700/50 text-yellow-500 bg-yellow-900/10">
                Path Restricted
              </Badge>
            )}
          </div>
          {!isFreeUser && (
            (user || isDemo) ? (
              <Button 
                onClick={exportToPdf} 
                className="mt-6 text-white font-medium px-6 py-3 rounded-xl transition-all hover:-translate-y-0.5" 
                style={{ 
                  background: '#3B82F6',
                  boxShadow: '0 6px 20px rgba(59,130,246,0.35)'
                }}
                data-testid="export-pdf-btn"
              >
                <Download className="w-4 h-4 mr-2" /> Export to PDF
              </Button>
            ) : (
              <Button 
                onClick={() => setShowLoginModal(true)} 
                className="mt-6 text-zinc-300 font-medium px-6 py-3 rounded-xl transition-all hover:-translate-y-0.5" 
                style={{ 
                  background: 'rgba(255,255,255,0.08)',
                  border: '1px solid rgba(255,255,255,0.1)'
                }}
                data-testid="export-pdf-btn-locked"
              >
                <Lock className="w-4 h-4 mr-2" /> Export to PDF
              </Button>
            )
          )}
        </div>

        {report.change_verification?.available && <ChangeVerification data={report.change_verification} />}
        <GoogleSearchEvidence data={report.search_reality} />

        {/* Login Required Modal for PDF Export */}
        <Dialog open={showLoginModal} onOpenChange={setShowLoginModal}>
          <DialogContent className="max-w-md rounded-2xl" style={{ background: '#111826', border: '1px solid rgba(255,255,255,0.04)' }}>
            <DialogHeader>
              <DialogTitle className="text-white flex items-center gap-2">
                <Lock className="w-5 h-5" style={{ color: '#3B82F6' }} />
                Login Required
              </DialogTitle>
              <DialogDescription className="text-[#9CA3AF] pt-2">
                PDF export is available only for registered users. Create a free account to export this report and save it to your dashboard.
              </DialogDescription>
            </DialogHeader>
            <div className="flex flex-col gap-3 mt-4">
              <Link to="/login" onClick={() => {
                localStorage.setItem('pendingClaimAuditId', id);
                setShowLoginModal(false);
              }}>
                <Button 
                  className="w-full text-white font-medium py-3 rounded-xl transition-all hover:-translate-y-0.5"
                  style={{ background: '#3B82F6', boxShadow: '0 6px 20px rgba(59,130,246,0.35)' }}
                >
                  <LogIn className="w-4 h-4 mr-2" /> Login / Register
                </Button>
              </Link>
              <Button 
                variant="ghost" 
                onClick={() => setShowLoginModal(false)} 
                className="text-[#6B7280] hover:text-[#9CA3AF] rounded-xl"
              >
                Maybe later
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* FREE BASELINE: paywall prompt (fallback only) */}
        {isFreeUser && !hasBusinessReport && (
          <div className="mb-6">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 mb-8" data-testid="issue-counts">
              {[
                { label: 'Technical Issues', count: report.issue_counts?.technical || 0, color: '#EF4444' },
                { label: 'On-page Issues', count: report.issue_counts?.on_page || 0, color: '#F97316' },
                { label: 'AI Readiness Gaps', count: report.issue_counts?.ai_readiness || 0, color: '#F59E0B' },
                { label: 'Trust Gaps', count: report.issue_counts?.trust || 0, color: '#8B5CF6' }
              ].map((item, i) => (
                <div 
                  key={i} 
                  className="p-6 rounded-2xl text-center transition-all hover:-translate-y-1"
                  style={{ 
                    background: '#151C2B',
                    border: '1px solid rgba(255,255,255,0.04)',
                    boxShadow: '0 10px 30px rgba(0,0,0,0.35)'
                  }}
                >
                  <div className="text-[40px] font-semibold" style={{ color: item.color, letterSpacing: '-1px' }}>{item.count}</div>
                  <div className="text-[13px] text-[#9CA3AF] uppercase tracking-wider mt-2">{item.label}</div>
                </div>
              ))}
            </div>

            {/* Legacy score cards for free reports */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-6 mb-8">
              {[
                { label: 'SEO Score', score: scores.seo_score, icon: Search, accentColor: '#3B82F6' },
                { label: 'AI Readiness', score: scores.ai_readiness_score, icon: Brain, accentColor: '#8B5CF6' },
                { label: 'Tech Health', score: scores.technical_health_score, icon: Shield, accentColor: '#14B8A6' },
                { label: 'Content', score: scores.content_quality_score, icon: FileText, accentColor: '#F59E0B' },
                { label: 'Trust', score: scores.trust_evidence_score, icon: TrendingUp, accentColor: '#22C55E' },
              ].map((s, i) => {
                const Icon = s.icon;
                const sc = s.score || 0;
                const scoreCol = sc >= 75 ? '#22C55E' : sc >= 50 ? '#F59E0B' : sc >= 25 ? '#F97316' : '#EF4444';
                return (
                  <div 
                    key={i} 
                    className="flex flex-col items-center p-6 rounded-2xl transition-all hover:-translate-y-1"
                    style={{ 
                      background: '#151C2B',
                      border: '1px solid rgba(255,255,255,0.04)',
                      boxShadow: '0 10px 30px rgba(0,0,0,0.35)'
                    }}
                  >
                    <div 
                      className="w-9 h-9 rounded-full flex items-center justify-center mb-3"
                      style={{ background: `${s.accentColor}12` }}
                    >
                      <Icon className="w-4.5 h-4.5" style={{ color: s.accentColor }} />
                    </div>
                    <div className="text-[40px] font-semibold" style={{ color: scoreCol, letterSpacing: '-1px' }}>{sc}</div>
                    <div className="text-[13px] text-[#9CA3AF] uppercase tracking-wider mt-2 text-center">{s.label}</div>
                    {/* Progress bar */}
                    <div className="w-full mt-4 h-1.5 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.06)' }}>
                      <div 
                        className="h-full rounded-full"
                        style={{ 
                          width: `${sc}%`,
                          background: `linear-gradient(90deg, ${scoreCol}, ${scoreCol}CC)`
                        }} 
                      />
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Business Overview - proof that we understand the site */}
            {report.business_analysis?.overview && (
              <Card 
                className="mb-8 rounded-[20px]"
                style={{ 
                  background: '#111826',
                  border: '1px solid rgba(255,255,255,0.04)',
                  boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.04), 0 0 60px -20px rgba(59,130,246,0.3)'
                }}
              >
                <CardContent className="p-6 sm:p-10">
                  <div className="flex items-center gap-3 mb-6">
                    <div 
                      className="w-10 h-10 rounded-xl flex items-center justify-center"
                      style={{ background: 'rgba(59,130,246,0.12)' }}
                    >
                      <Brain className="w-5 h-5" style={{ color: '#3B82F6' }} />
                    </div>
                    <h3 className="text-lg font-semibold text-white tracking-wide">Business Overview</h3>
                    <span 
                      className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium"
                      style={{ background: 'rgba(59,130,246,0.15)', color: '#3B82F6' }}
                    >
                      AI Analysis
                    </span>
                  </div>
                  
                  {report.business_analysis.overview.primary_goal && (
                    <div className="mb-6">
                      <div className="text-xs text-[#9CA3AF] uppercase tracking-wider mb-2">Primary Goal</div>
                      <p className="text-sm text-[#D1D5DB] leading-relaxed">{report.business_analysis.overview.primary_goal}</p>
                    </div>
                  )}
                  
                  {report.business_analysis.overview.business_model && (
                    <div>
                      <div className="text-xs text-[#9CA3AF] uppercase tracking-wider mb-2">Business Model</div>
                      <p className="text-sm text-[#D1D5DB] leading-relaxed">{report.business_analysis.overview.business_model}</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            <PaywallPanel />
          </div>
        )}

        {/* BUSINESS REPORT: Render if available (Full or Gated) */}
        {hasBusinessReport && (
          <BusinessReport
            report={{...report.business_analysis, market_intelligence: report.market_intelligence, detailed_actions: report.detailed_actions, ai_visibility_check: report.ai_visibility_check, action_plan: report.action_plan, security: report.security, core_web_vitals: report.core_web_vitals, technical: report.technical}}
            domain={request?.target?.normalized_domain || ''}
            competitive={report.competitive}
            isActionPlanGated={report.action_plan_gated || false}
            auditId={effectiveId}
            canViewCodeFixes={report.can_view_code_fixes === true}
            crawledUrls={report.crawl?.url_samples?.fetched_examples || report.crawl?.sample_urls || []}
            language={auditLanguage}
          />
        )}

        {/* PAID FULL: fallback when business_analysis not available */}
        {!isFreeUser && !hasBusinessReport && (
          <div className="space-y-8">
            <Card 
              className="rounded-[20px]"
              style={{ 
                background: '#111826',
                border: '1px solid rgba(255,255,255,0.04)'
              }}
            >
              <CardContent className="p-6 sm:p-12 text-center">
                <div 
                  className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-6"
                  style={{ background: 'rgba(107,114,128,0.1)' }}
                >
                  <Brain className="w-8 h-8 text-[#6B7280]" />
                </div>
                <h3 className="text-[#D1D5DB] text-lg font-semibold">Business Analysis Pending</h3>
                <p className="text-[#6B7280] text-sm mt-3 max-w-md mx-auto leading-relaxed">
                  The AI-powered business analysis is being generated. This report was produced before the Business Analysis Engine was available.
                </p>
                {report.business_analysis?._error && (
                  <p className="text-red-400 text-xs mt-4 font-mono">{report.business_analysis._error}</p>
                )}
              </CardContent>
            </Card>
          </div>
        )}


      </div>
    </div>
  );

  // For sample report (demo), show without AppLayout
  if (isDemo) {
    return content;
  }

  // For logged-in users, wrap in AppLayout with sidebar
  if (user) {
    return (
      <AppLayout breadcrumbs={[
        { label: 'Dashboard', href: '/dashboard' },
        { label: `${domainName}${auditDate ? ` (${auditDate})` : ''}` }
      ]}>
        <Helmet>
          <title>Audit Report | ScoreCite</title>
          <meta name="robots" content="noindex, nofollow" />
        </Helmet>
        {content}
      </AppLayout>
    );
  }

  // Store audit ID for claiming after login
  const handleSaveToAccount = () => {
    localStorage.setItem('pendingClaimAuditId', id);
  };

  // For non-logged users viewing shared report - show with GlobalNav and CTA banner
  return (
    <>
      <GlobalNav />
      <Helmet>
        <title>Audit Report | ScoreCite</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      
      {/* CTA Banner for non-logged users */}
      <div className="bg-gradient-to-r from-blue-600/20 to-purple-600/20 border-b border-blue-500/20">
        <div className="w-full px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3 text-center sm:text-left">
              <Zap className="w-5 h-5 text-blue-400 hidden sm:block" />
              <div>
                <p className="text-white font-medium">Save this audit to your dashboard</p>
                <p className="text-zinc-400 text-sm">Start monitoring your website's health and track improvements over time</p>
              </div>
            </div>
            <Link to="/login" onClick={handleSaveToAccount}>
              <Button className="bg-blue-600 hover:bg-blue-500 text-white whitespace-nowrap">
                <LogIn className="w-4 h-4 mr-2" /> Login / Register
              </Button>
            </Link>
          </div>
        </div>
      </div>
      
      {content}
    </>
  );
}
